import {
  BoxGeometry,
  BufferGeometry,
  ConeGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
  type Material,
} from "three";

import { VILNIUS_STATION_INTERCHANGE } from "../../config/stationInterchange.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface StationInterchangeView {
  readonly object: Group;
  dispose(): void;
}

const GLYPHS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  " ": ["00000", "00000", "00000", "00000", "00000", "00000", "00000"],
  A: ["01110", "10001", "10001", "11111", "10001", "10001", "10001"],
  B: ["11110", "10001", "10001", "11110", "10001", "10001", "11110"],
  E: ["11111", "10000", "10000", "11110", "10000", "10000", "11111"],
  G: ["01111", "10000", "10000", "10111", "10001", "10001", "01111"],
  I: ["01110", "00100", "00100", "00100", "00100", "00100", "01110"],
  K: ["10001", "10010", "10100", "11000", "10100", "10010", "10001"],
  L: ["10000", "10000", "10000", "10000", "10000", "10000", "11111"],
  N: ["10001", "11001", "10101", "10011", "10001", "10001", "10001"],
  O: ["01110", "10001", "10001", "10001", "10001", "10001", "01110"],
  R: ["11110", "10001", "10001", "11110", "10100", "10010", "10001"],
  S: ["01111", "10000", "10000", "01110", "00001", "00001", "11110"],
  T: ["11111", "00100", "00100", "00100", "00100", "00100", "00100"],
  U: ["10001", "10001", "10001", "10001", "10001", "10001", "01110"],
  V: ["10001", "10001", "10001", "10001", "10001", "01010", "00100"],
  W: ["10001", "10001", "10001", "10101", "10101", "10101", "01010"],
  Y: ["10001", "10001", "01010", "00100", "00100", "00100", "00100"],
  Z: ["11111", "00001", "00010", "00100", "01000", "10000", "11111"],
});

function bitmapLabelGeometry(
  value: string,
  maximumWidthM: number,
  maximumHeightM: number,
): BufferGeometry {
  const text = value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toUpperCase();
  const columns = Math.max(1, text.length * 6 - 1);
  const pixel = Math.min(maximumWidthM / columns, maximumHeightM / 7);
  const width = columns * pixel;
  const height = 7 * pixel;
  const positions: number[] = [];
  const indices: number[] = [];
  const appendFace = (
    left: number,
    right: number,
    bottom: number,
    top: number,
    z: number,
    back: boolean,
  ) => {
    const start = positions.length / 3;
    positions.push(left, bottom, z, right, bottom, z, right, top, z, left, top, z);
    indices.push(...(back
      ? [start, start + 2, start + 1, start, start + 3, start + 2]
      : [start, start + 1, start + 2, start, start + 2, start + 3]));
  };
  for (const [characterIndex, character] of [...text].entries()) {
    const glyph = GLYPHS[character] ?? GLYPHS[" "]!;
    for (const [rowIndex, row] of glyph.entries()) {
      for (let columnIndex = 0; columnIndex < 5; columnIndex += 1) {
        if (row[columnIndex] !== "1") continue;
        const left = -width / 2 + (characterIndex * 6 + columnIndex) * pixel;
        const right = left + pixel * 0.82;
        const top = height / 2 - rowIndex * pixel;
        const bottom = top - pixel * 0.82;
        appendFace(left, right, bottom, top, 0.061, false);
        appendFace(-right, -left, bottom, top, -0.061, true);
      }
    }
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}

function mesh(
  name: string,
  geometry: BoxGeometry | ConeGeometry,
  material: Material,
  position: readonly [number, number, number],
): Mesh {
  const object = new Mesh(geometry, material);
  object.name = name;
  object.position.set(...position);
  object.castShadow = false;
  object.receiveShadow = true;
  return object;
}

export function createStationInterchangeView(
  materials: CityMaterialLibrary,
): StationInterchangeView {
  const root = new Group();
  root.name = "vilnius-station-pedestrian-interchange";
  root.userData = {
    capturedAt: VILNIUS_STATION_INTERCHANGE.capturedAt,
    sourceLayer: VILNIUS_STATION_INTERCHANGE.sourceLayer,
    license: VILNIUS_STATION_INTERCHANGE.license,
    sourceWayIds: VILNIUS_STATION_INTERCHANGE.points.map(({ osmWayId }) => osmWayId),
    representation: "authored-wayfinding-on-source-backed-pedestrian-paths",
  };

  for (const point of VILNIUS_STATION_INTERCHANGE.points) {
    const marker = new Group();
    marker.name = `station-interchange:${point.id}`;
    marker.position.set(point.world.x, point.world.y, point.world.z);
    marker.rotation.y = point.yawRadians;
    marker.userData = {
      osmWayId: point.osmWayId,
      sourceKind: point.sourceKind,
      labels: [...point.labels],
    };

    if (point.id === "marked-crossing") {
      for (let index = -2; index <= 2; index += 1) {
        marker.add(mesh(
          `${marker.name}:crossing-stripe-${index + 2}`,
          new BoxGeometry(0.58, 0.028, 4.5),
          materials.roadMarking,
          [index * 1.12, 0.014, 0],
        ));
      }
      for (const [index, side] of [-1, 1].entries()) {
        marker.add(
          mesh(
            `${marker.name}:beacon-${index}`,
            new BoxGeometry(0.1, 2.4, 0.1),
            materials.streetPole,
            [side * 3.6, 1.2, 0],
          ),
          mesh(
            `${marker.name}:beacon-light-${index}`,
            new BoxGeometry(0.22, 0.22, 0.22),
            materials.lampBulb,
            [side * 3.6, 2.28, 0],
          ),
        );
      }
    } else {
      marker.add(
        mesh(
          `${marker.name}:pole`,
          new BoxGeometry(0.12, 3.1, 0.12),
          materials.streetPole,
          [0, 1.55, 0],
        ),
        mesh(
          `${marker.name}:lithuanian-panel`,
          new BoxGeometry(3.6, 0.62, 0.11),
          materials.busStopBlue,
          [1.45, 2.72, 0],
        ),
        mesh(
          `${marker.name}:english-panel`,
          new BoxGeometry(3.0, 0.42, 0.1),
          materials.busStopIvory,
          [1.15, 2.18, 0],
        ),
      );
      const lithuanianLabel = new Mesh(
        bitmapLabelGeometry(point.labels[0]!, 3.22, 0.4),
        materials.busStopIvory,
      );
      lithuanianLabel.name = `${marker.name}:lithuanian-label`;
      lithuanianLabel.position.set(1.45, 2.72, 0);
      const englishLabel = new Mesh(
        bitmapLabelGeometry(point.labels[1]!, 2.68, 0.25),
        materials.busStopBlue,
      );
      englishLabel.name = `${marker.name}:english-label`;
      englishLabel.position.set(1.15, 2.18, 0);
      marker.add(lithuanianLabel, englishLabel);
      const arrow = mesh(
        `${marker.name}:direction-arrow`,
        new ConeGeometry(0.32, 0.75, 3),
        materials.busStopIvory,
        [3.48, 2.72, 0],
      );
      arrow.rotation.z = -Math.PI / 2;
      marker.add(arrow);
    }
    root.add(marker);
  }

  return {
    object: root,
    dispose(): void {
      root.traverse((object) => {
        if (object instanceof Mesh) {
          object.geometry.dispose();
        }
      });
      root.removeFromParent();
    },
  };
}
